import React from 'react'
import { useEffect, useState } from 'react'
import axios from 'axios'
import { TableCell, TableRow, IconButton, CircularProgress, Typography } from '@mui/material'
import ProcessTimeline from './ProcessTimeline'
import { GetApp, Visibility } from '@mui/icons-material'
import { useAuthContext } from '../../hooks/useAuthContext'
import { FIELD_TYPES, FORMATS } from '../../utility/constants'
import moment from 'moment'
import LongMenu from '../../pages/DataEntryPage/Fields/LongMenu'
import FileViewer from './PreviewComponent/components/FileViewer'
import { activityTemplateService } from '../../services/activityTemplateService'
import { formatCurrency } from '../../pages/DataEntryPage/utils/currency'
import MultilineEllipsisWithTooltip from './MultilineEllipsisWithTooltip'
import { extractDisplayName } from '../../utility/fileUtil'
import TimeStampedBigTextViewer from './TimeStampedBigTextViewer'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

const ProcessDashboardValues = ({
  processData,
  process_template_id,
  hiddenColumns,
  currencyForDashboardValues,
  hiddenActivityColumns,
  processTemplates,
  processDataForProcessTemplate,
  companyDetails,
  usersList,
}) => {
  const { user } = useAuthContext()
  const [loading, setLoading] = useState(new Set())
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false)
  const [selectedFileKey, setSelectedFileKey] = useState(null)
  const [templateRoutes, setTemplateRoutes] = useState(null)
  const [isTSViewerOpen, setIsTSViewerOpen] = useState(false)
  const [tsViewerEntries, setTsViewerEntries] = useState([])
  const [tsViewerTitle, setTsViewerTitle] = useState('')

  // Get user name from update
  const getUserName = (update) => {
    try {
      if (!update?.user_id || !Array.isArray(usersList)) {
        return ''
      }

      const user = usersList.find((u) => u.id === update.user_id)
      return user?.value?.replace(/\s*\([^)]*\)\s*/g, '').trim() ?? ''
    } catch (error) {
      console.error('Error in getUserName:', error)
      return ''
    }
  }

  const handlePreviewClick = (fileKey) => {
    setSelectedFileKey(fileKey)
    setIsFileViewerOpen(true)
  }

  const openTimestampedViewer = (entries, title) => {
    setTsViewerEntries(Array.isArray(entries) ? entries : [])
    setTsViewerTitle(title || 'Timestamped Entries')
    setIsTSViewerOpen(true)
  }

  const closeTimestampedViewer = () => {
    setIsTSViewerOpen(false)
    setTsViewerEntries([])
    setTsViewerTitle('')
  }

  const getProcessTimelineBadgeValue = (process_reference_num) => {
    return (processDataForProcessTemplate?.filter(
      // eslint-disable-next-line eqeqeq
      (it) => it.process_reference_num == process_reference_num, // Important: Don't change to strict equality check
    )).length
  }

  let axiosOptionsWithBlob = {}
  if (user) {
    axiosOptionsWithBlob = {
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
      responseType: 'blob',
    }
  }

  useEffect(() => {
    let getRouteTemplatesData = async () => {
      const routeTemplatesOfProcess = (
        await activityTemplateService.getProcessAllRoutes(user, process_template_id)
      ).data

      setTemplateRoutes(routeTemplatesOfProcess)
    }
    if (process_template_id) {
      getRouteTemplatesData()
    }
  }, [process_template_id, user])

  const getFileWithS3Key = async (s3Key, index) => {
    setLoading((prev) => {
      return new Set([...prev, index])
    })

    const result = await axios.get(
      `${NODE_BASE_URL}/data-entry/file/${s3Key}`,
      axiosOptionsWithBlob,
    )

    const url = window.URL.createObjectURL(new Blob([result.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', extractDisplayName(s3Key))
    document.body.appendChild(link)
    link.click()

    setLoading((prev) => {
      const updated = new Set(prev)
      updated.delete(index)
      return updated
    })
  }

  const options = (x, y) => {
    return [
      {
        label: 'Preview',
        icon: (
          <IconButton size="small">
            <Visibility />
          </IconButton>
        ),
        onClick: () => handlePreviewClick(x),
      },
      {
        label: 'Download',
        icon: (
          <IconButton size="small">
            {loading.has(y) ? (
              <CircularProgress color="info" size={'25px'} />
            ) : (
              <GetApp color="info" size="small" />
            )}
          </IconButton>
        ),
        onClick: () => getFileWithS3Key(x, y),
      },
    ]
  }

  const valuesArray = []
  for (const arr of processData) {
    valuesArray.push(arr.map((it) => it.value))
  }

  const toMultilineText = (value, maxItems = 3) => {
    if (!value) return ''
    const values = Array.isArray(value) ? value : [value]
    return (
      <span style={{ whiteSpace: 'line-break' }}>{values.slice(0, maxItems).join('\n\n')}</span>
    )
  }

  return (
    <>
      {processData.map((pdr, j) => {
        const rowBg = '#fff'
        return (
          <TableRow
            className="billog-quanity-inside-table"
            key={j}
            style={j % 2 != 0 ? { backgroundColor: '#fff' } : null}
          >
            <>
              {pdr.map((pd, i) => {
                // get non default currencies against field id
                const fieldCurrencyMap = currencyForDashboardValues
                  .filter((currency) => currency.process_reference_num === pd.process_reference_num)
                  ?.reduce((acc, cur) => {
                    return { ...acc, [cur.field_template_id]: cur.currency_code }
                  }, {})

                const isHidden =
                  pd.is_hidden ||
                  (pd?.is_Activity
                    ? hiddenActivityColumns?.includes(pd.id)
                    : hiddenColumns?.includes(pd.id))

                // Sticky styles for Info (i=0) and Reference Number (i=1)
                const stickyStyle =
                  i === 0
                    ? {
                        position: 'sticky',
                        left: 0,
                        backgroundColor: rowBg,
                        zIndex: 2,
                      }
                    : i === 1
                      ? {
                          position: 'sticky',
                          left: '50px',
                          backgroundColor: rowBg,
                          zIndex: 2,
                        }
                      : {}

                if (i == 0) {
                  return (
                    <TableCell key={i} id={`${j}-${i}`} sx={stickyStyle}>
                      <ProcessTimeline
                        processReferanceNum={pd.value}
                        processTemplateId={process_template_id}
                        templateRoutes={templateRoutes}
                        processTemplates={processTemplates}
                        processTimeLineBadge={getProcessTimelineBadgeValue(pd.value)}
                      />
                    </TableCell>
                  )
                } else if (i > 0 && !isHidden) {
                  if (!isHidden) {
                    if (pd.fieldType === FIELD_TYPES.FILE && pd.value.trim() !== '') {
                      return (
                        <TableCell id={`${j}-${i}`} key={i} sx={stickyStyle}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            {extractDisplayName(pd.value)}
                            <LongMenu options={options(pd.value, j)} />
                          </div>
                        </TableCell>
                      )
                    } else if (pd.fieldType === FIELD_TYPES.TIMESTAMPED_BIGTEXT) {
                      const entries = Array.isArray(pd?.value) ? pd.value : []
                      const firstEntry = entries[0]
                      const username = firstEntry ? getUserName(firstEntry) : ''
                      const moreSuffix = entries.length > 1 ? ` (+${entries.length - 1} more)` : ''

                      return (
                        <TableCell
                          key={i}
                          sx={stickyStyle}
                          onClick={() => openTimestampedViewer(entries, pd?.label || pd?.name)}
                          style={{ cursor: entries.length ? 'pointer' : 'default' }}
                        >
                          <Typography
                            variant="caption"
                            style={{
                              whiteSpace: 'normal',
                              overflowWrap: 'break-word',
                              wordBreak: 'break-word',
                              display: 'block',
                              minWidth: 320,
                            }}
                          >
                            {firstEntry ? (
                              <span>
                                <span
                                  style={{ fontWeight: 700 }}
                                >{`${firstEntry.timestamp}:`}</span>
                                {` ${firstEntry.text}`}
                                {username ? (
                                  <span style={{ fontStyle: 'italic' }}>{` - ${username}`}</span>
                                ) : (
                                  ''
                                )}
                                {moreSuffix ? (
                                  <span style={{ fontStyle: 'italic' }}>{moreSuffix}</span>
                                ) : (
                                  ''
                                )}
                              </span>
                            ) : (
                              ''
                            )}
                          </Typography>
                        </TableCell>
                      )
                    } else if (pd.fieldType === FIELD_TYPES.CURRENCY) {
                      return (
                        <TableCell id={`${j}-${i}`} key={i} align="right" sx={stickyStyle}>
                          <Typography variant="caption">
                            {formatCurrency(pd.value, fieldCurrencyMap[pd.id], {
                              defaultCurrency: companyDetails?.default_currency_code,
                            })}
                          </Typography>
                        </TableCell>
                      )
                    } else if (
                      pd.fieldType === FIELD_TYPES.LIST ||
                      pd.fieldType === FIELD_TYPES.LIST_MULTI
                    ) {
                      return (
                        <TableCell id={`${j}-${i}`} key={i} align="left" sx={stickyStyle}>
                          <Typography variant="caption" style={{ whiteSpace: 'normal' }}>
                            <MultilineEllipsisWithTooltip
                              text={Array.isArray(pd.value) ? pd.value.join('\n\n') : pd.value}
                              maxLines={3}
                            />
                          </Typography>
                        </TableCell>
                      )
                    } else if (pd.fieldType === FIELD_TYPES.DATE) {
                      return (
                        <TableCell
                          className="bilof-quantity-date"
                          id={`${j}-${i}`}
                          key={i}
                          align="right"
                          sx={stickyStyle}
                        >
                          <Typography variant="caption">
                            {moment(new Date(pd.value)).format('DD MMM YYYY') != 'Invalid date'
                              ? moment(new Date(pd.value)).format('DD MMM YYYY')
                              : ''}
                          </Typography>
                        </TableCell>
                      )
                    } else if (pd.fieldType === FIELD_TYPES.DATETIME) {
                      return (
                        <TableCell
                          className="bilof-quantity-datetime"
                          id={`${j}-${i}`}
                          key={i}
                          align="right"
                          sx={stickyStyle}
                        >
                          <Typography variant="caption">
                            {moment(new Date(pd.value)).format(FORMATS.DATETIME) != 'Invalid date'
                              ? moment(new Date(pd.value)).format(FORMATS.DATETIME)
                              : ''}
                          </Typography>
                        </TableCell>
                      )
                    } else {
                      return (
                        <>
                          {!pd.is_hidden ? (
                            <TableCell
                              id={`${j}-${i}`}
                              key={i}
                              align={pd.fieldType === FIELD_TYPES.NUMBER ? 'right' : 'left'}
                              sx={stickyStyle}
                            >
                              {!pd.is_hidden ? (
                                <Typography variant="caption" style={{ whiteSpace: 'pre-line' }}>
                                  <MultilineEllipsisWithTooltip text={pd.value} maxLines={3} />
                                </Typography>
                              ) : null}
                            </TableCell>
                          ) : null}
                        </>
                      )
                    }
                  }
                } else {
                  return <></>
                }
              })}
            </>
          </TableRow>
        )
      })}

      <FileViewer
        open={isFileViewerOpen}
        onClose={() => setIsFileViewerOpen(false)}
        fileKey={selectedFileKey}
        user={user}
        onDownload={
          selectedFileKey ? () => getFileWithS3Key(selectedFileKey, selectedFileKey) : undefined
        }
      />

      <TimeStampedBigTextViewer
        open={isTSViewerOpen}
        onClose={closeTimestampedViewer}
        title={tsViewerTitle}
        entries={tsViewerEntries}
        resolveUserName={getUserName}
      />
    </>
  )
}
export default React.memo(ProcessDashboardValues)
